import { useEffect } from 'react'
import { useFormikContext } from 'formik'

import { BUTTON_HEIGHT_40 } from '../../../Button'
import { FormBody, FormInlineNotifications } from '../../../Form'
import { Content } from '../../../Modal'
import { useInvoiceModal } from '../../InvoiceModal.hooks'
import {
  getInvoiceModalTrimmedValues,
  getVisibleInvoiceModalFields,
  mergeInvoiceModalParsedValues,
} from '../../InvoiceModal.utils'
import { InvoiceModalFormField } from '../../InvoiceModalFormField'

import {
  useInvoiceModalFileUpload,
  useInvoiceModalParsingNotifications,
} from './FormFields.hooks'
import { FormFieldsFooterActions } from './FormFieldsFooterActions'
import { FormFieldsPreview } from './FormFieldsPreview'

import * as styles from './FormFields.module.css'

function FormFields({
  documentId,
  fields,
  files,
  isFileChanged = false,
  requestId,
  savedDocumentId = null,
  submitTitleKey = 'Modal.CreateBtnKey',
  withFileUpload,
  onCancel,
  onDocumentIdChange,
}) {
  const { dirty, isSubmitting, setValues, submitCount, values } =
    useFormikContext()

  const {
    handleParse,
    hasDomondaKey,
    hasParsingError,
    isParsing,
    isPdfLoading,
    parsedValues,
    pdfUrl,
  } = useInvoiceModal({ documentId, requestId, savedDocumentId })

  const { handleFinishUpload, handleStartUpload, isUploading } =
    useInvoiceModalFileUpload({ onDocumentIdChange })

  const { notifications } = useInvoiceModalParsingNotifications({
    hasParsingError,
  })

  useEffect(() => {
    if (parsedValues) {
      setValues(prev => mergeInvoiceModalParsedValues(prev, parsedValues))
    }
  }, [parsedValues])

  useEffect(() => {
    if (submitCount > 0) {
      setValues(prev => getInvoiceModalTrimmedValues(prev, fields))
    }
  }, [submitCount])

  const visibleFields = getVisibleInvoiceModalFields(fields, values)

  function handleCancel() {
    onCancel({
      dirty,
      files,
    })
  }

  function handleDeleteFile() {
    onDocumentIdChange(null)
  }

  const pdfFile = pdfUrl ? { origin: pdfUrl } : null

  const isFileActionDisabled = isParsing || isSubmitting || isUploading

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: {
          className: styles.footerButton,
          height: BUTTON_HEIGHT_40,
          onClick: handleCancel,
        },
        children: (
          <FormFieldsFooterActions
            documentId={documentId}
            hasDomondaKey={hasDomondaKey}
            isFileActionDisabled={isFileActionDisabled}
            isParsing={isParsing}
            isPdfLoading={isPdfLoading}
            isUploading={isUploading}
            withFileUpload={withFileUpload}
            onDeleteFile={handleDeleteFile}
            onFinishUpload={handleFinishUpload}
            onParse={handleParse}
            onStartUpload={handleStartUpload}
          />
        ),
        className: styles.actions,
        isSubmitting,
        submitProps: {
          className: styles.footerButton,
          disabled:
            (!dirty && !isFileChanged) ||
            isPdfLoading ||
            isParsing ||
            isSubmitting ||
            isUploading,
          height: BUTTON_HEIGHT_40,
          isLoading: isSubmitting,
          title: submitTitleKey,
        },
      }}
    >
      <Content
        isFullScreen
        className={styles.content}
        containerClassName={styles.contentContainer}
        contentWrapperClassName={styles.contentWrapper}
      >
        <FormInlineNotifications
          className={styles.parsingNotification}
          notifications={notifications}
        />
        <div className={styles.panels}>
          <div className={styles.formPanel}>
            {visibleFields.map(field => (
              <InvoiceModalFormField
                disabled={isParsing}
                field={field}
                key={field.name}
              />
            ))}
          </div>
          <FormFieldsPreview
            isPdfLoading={isPdfLoading}
            isUploadDisabled={isFileActionDisabled}
            isUploading={isUploading}
            pdfFile={pdfFile}
            withFileUpload={withFileUpload}
            onFinishUpload={handleFinishUpload}
            onStartUpload={handleStartUpload}
          />
        </div>
      </Content>
    </FormBody>
  )
}

export default FormFields
